/**
 * Tracker factory function.
 * Creates a tracker instance with pre-bound configuration (bookendName, locale).
 * This eliminates the need to pass common arguments to every tracking call.
 *
 * @example
 * // Initialize once in web/index.tsx
 * import { initTracking } from './tracking'
 * import { ComponentType } from '../types/components-map'
 *
 * initTracking('en-US', ComponentType.HEADER)
 *
 * // Use in components without passing bookendName/locale every time
 * getHeaderTracker().trackLink({
 *   navigationDetailPath: ['business-cards', 'premium'],
 *   href: '/products/business-cards',
 * })
 */

import type { Tracker, TrackerConfig, TrackLinkOptions, TrackHeaderLinkOptions } from '../types/tracking'
import { ComponentType } from '../types/components-map'
import { buildNavigationDetail, getDestinationPath } from './config'
import { trackNavigationInternal, trackHeaderLinkInternal } from './navigation'
import { trackFlyOutView as flyOutView, trackFlyOutClick as flyOutClick } from './flyout'
import { trackVatOption as vatOption, trackNavHover as navHover, trackPopInteraction as popInteraction, resetTracking as reset } from './misc'
import { DEFAULT_LOCALE } from '../constants/config'

/**
 * Creates a tracker instance with pre-bound configuration.
 *
 * @param config - The tracker configuration
 * @param config.bookendName - The bookend name (e.g., 'header', 'footer')
 * @param config.locale - The locale (e.g., 'en-US', 'de-DE')
 * @returns A Tracker instance with all tracking methods bound to the configuration
 *
 * @example
 * const headerTracker = createTracker({ bookendName: 'header', locale: 'en-US' })
 *
 * // Track a navigation link click
 * headerTracker.trackLink({
 *   subSection: 'TopNav',
 *   navigationDetailPath: ['business-cards', 'premium'],
 *   href: '/products/business-cards',
 *   linkType: 'Text Layout Link',
 *   navigationSection: CATEGORY_SECTION,
 * })
 *
 * // Track a fly-out view (locale automatically appended)
 * headerTracker.trackFlyOutView('MobileMenu', 'menu-opened')
 */
export const createTracker = (config: TrackerConfig): Tracker => {
  const { bookendName, locale } = config

  return {
    // Expose config for inspection
    bookendName,
    locale,

    /**
     * Tracks a navigation link click.
     * The bookendName is automatically applied from initialization.
     */
    trackLink: (options: TrackLinkOptions): void => {
      const { subSection, navigationDetailPath, linkType, navigationSection, href } = options

      const navigationDetail = buildNavigationDetail(navigationSection, navigationDetailPath)
      const destinationPath = getDestinationPath(href)

      trackNavigationInternal(bookendName, navigationDetail, destinationPath, subSection, linkType, navigationSection)
    },

    /**
     * Tracks a header link click (accordion/menu opens, etc.)
     * The bookendName is automatically applied from initialization.
     */
    trackHeaderLink: (options: TrackHeaderLinkOptions): void => {
      const { subSection, navigationDetailPath, navigationSection } = options

      const navigationDetail = buildNavigationDetail(navigationSection, navigationDetailPath)

      trackHeaderLinkInternal(bookendName, navigationDetail, subSection, navigationSection)
    },

    /**
     * Tracks a fly-out viewed event.
     * The locale is automatically appended to eventDetail if provided.
     */
    trackFlyOutView: (label: string, eventDetail?: string): void => {
      flyOutView(label, eventDetail, locale)
    },

    /**
     * Tracks a fly-out clicked event.
     * The locale is automatically appended to eventDetail if provided.
     */
    trackFlyOutClick: (label: string, eventDetail?: string): void => {
      flyOutClick(label, eventDetail, locale)
    },

    /**
     * Tracks a VAT option selection event.
     */
    trackVatOption: (label: string): void => {
      vatOption(label)
    },

    /**
     * Tracks a navigation hover event.
     * The locale is automatically applied from initialization.
     * Note: Only fires for de-DE locale.
     */
    trackNavHover: (analyticsId: string): void => {
      navHover(analyticsId, locale)
    },

    /**
     * Tracks pop-up interaction events.
     */
    trackPopInteraction: (action: string, label: string, eventDetail?: string): void => {
      popInteraction(action, label, eventDetail)
    },

    /**
     * Resets tracking state (typically called on sign-out).
     */
    resetTracking: (): void => {
      reset()
    },
  }
}

// Singleton instances for header and footer trackers
let headerTrackerInstance: Tracker | null = null
let footerTrackerInstance: Tracker | null = null
let defaultLocale = DEFAULT_LOCALE

/**
 * Initializes tracking for the application.
 * Call this once during app initialization (e.g., in web/index.tsx).
 *
 * @param locale - The locale to use for tracking (e.g., 'en-US')
 * @param componentType - The component type (ComponentType.HEADER or ComponentType.FOOTER)
 * @returns The initialized tracker instance
 *
 * @example
 * // In web/index.tsx
 * initTracking('en-US', ComponentType.HEADER)
 *
 * // In components - use getHeaderTracker() or getFooterTracker()
 * const tracker = getHeaderTracker()
 * tracker.trackLink({ ... })
 */
export const initTracking = (locale: string, componentType: ComponentType): Tracker => {
  defaultLocale = locale

  if (componentType === ComponentType.HEADER) {
    headerTrackerInstance = createTracker({ bookendName: componentType, locale })
    return headerTrackerInstance
  }

  footerTrackerInstance = createTracker({ bookendName: componentType, locale })
  return footerTrackerInstance
}

/**
 * Gets or creates the header tracker instance.
 * If the tracker doesn't exist, it creates one with the default locale.
 *
 * @returns The header tracker instance
 */
export const getHeaderTracker = (): Tracker => {
  headerTrackerInstance ??= createTracker({
    bookendName: ComponentType.HEADER,
    locale: defaultLocale,
  })
  return headerTrackerInstance
}

/**
 * Gets or creates the footer tracker instance.
 * If the tracker doesn't exist, it creates one with the default locale.
 *
 * @returns The footer tracker instance
 */
export const getFooterTracker = (): Tracker => {
  footerTrackerInstance ??= createTracker({
    bookendName: ComponentType.FOOTER,
    locale: defaultLocale,
  })
  return footerTrackerInstance
}

/**
 * Resets tracker instances (useful for testing).
 */
export const resetTrackerInstances = (): void => {
  headerTrackerInstance = null
  footerTrackerInstance = null
  defaultLocale = DEFAULT_LOCALE
}
